import { BEYOND_WORDS_SCRIPT } from "@config/beyondWords";
import { PLAYLIST_STICKY_ID } from "@elements/BeyondWordsPlayer/BeyondWordsPlaylist";
import { registerAnalyticsEvents } from "@elements/BeyondWordsPlayer/helper/registerAnalyticsEvents";
import { registerMediaControllerEvents } from "@elements/BeyondWordsPlayer/helper/registerMediaControllerEvents";
import { registerPlayerStyleEvents } from "@elements/BeyondWordsPlayer/helper/registerPlayerStyleEvents";
import { BEYOND_WORDS_PROJECT_ID } from "@helper/getEnvVariables";
import useScript from "@hooks/useScript/useScript";
import { BeyondWordsPlayerInstance } from "@typings/BeyondWords";
import { useEffect, useRef, useState } from "react";

import { useAudioAnalytics } from "../useAnalyticsCall/useAudioAnalytics";

export type PlaylistSourceItem = {
  sourceId: string;
  title: string;
};

type UseBeyondWordsPlaylistPlayerData = {
  playerRef: React.RefObject<HTMLDivElement | null>;
  playerInstance: React.RefObject<BeyondWordsPlayerInstance | null>;
  isPlaying: boolean;
};

type UseBeyondWordsPlaylistPlayerProps = {
  playlistItems: PlaylistSourceItem[];
  title?: string;
};

const PLAYLIST_INLINE_ID = "tts-playlist-inline-target";

export function useBeyondWordsPlaylistPlayer({
  playlistItems,
}: UseBeyondWordsPlaylistPlayerProps): UseBeyondWordsPlaylistPlayerData {
  const playerRef = useRef<HTMLDivElement | null>(null);
  const initializedRef = useRef(false);
  const playerInstanceRef = useRef<BeyondWordsPlayerInstance | null>(null);
  const bhAudioTracking = useAudioAnalytics();
  const playerRefId = useRef(
    `beyondwords-playlist-${Math.random().toString(36).substr(2, 9)}`,
  );
  const [isPlaying, setIsPlaying] = useState(false);

  const { isScriptReady } = useScript({
    src: BEYOND_WORDS_SCRIPT,
    id: "beyondwords-player-script",
    checkScriptIsLoaded: () =>
      typeof window !== "undefined" && !!window.BeyondWords,
  });

  useEffect(() => {
    if (!isScriptReady || !playerRef.current || initializedRef.current) return;
    if (!playlistItems.length) return;

    initializedRef.current = true;

    const inlineTarget = document.createElement("div");
    inlineTarget.className = PLAYLIST_INLINE_ID;
    playerRef.current.appendChild(inlineTarget);

    let cleanWidgetTarget: HTMLDivElement | null = null;
    const stickyContainer = document.getElementById(PLAYLIST_STICKY_ID);
    if (stickyContainer) {
      cleanWidgetTarget = document.createElement("div");
      cleanWidgetTarget.className = "bw-playlist-sticky-target";
      stickyContainer.appendChild(cleanWidgetTarget);
    }

    const player: BeyondWordsPlayerInstance = new window.BeyondWords.Player({
      target: inlineTarget,
      widgetTarget: cleanWidgetTarget || `#${PLAYLIST_STICKY_ID}`,
      widgetStyle: "standard",
      projectId: BEYOND_WORDS_PROJECT_ID,
      playlist: playlistItems.map(({ sourceId }) => ({
        sourceId,
      })),
      playlistStyle: "hide",
      playerStyle: "standard",
      clientSideEnabled: false,
      logoIconEnabled: false,
      playlistToggle: "show", // show playlist UI within the player
      downloadFormats: [],
      callToAction: "BH News Playlist",
      backgroundColor: "#F1F6FB",
      textColor: "#1E1E1E",
      iconColor: "#01519C",
      highlightSections: "none",
      clickableSections: "none",
    });

    playerInstanceRef.current = player;

    const { onClosedByUser } = registerPlayerStyleEvents(player, true);

    registerMediaControllerEvents(player, undefined, playerRefId.current);

    registerAnalyticsEvents(
      player,
      bhAudioTracking,
      () => playlistItems[player.contentIndex]?.title ?? "BH News Playlist",
      () => playlistItems[player.contentIndex]?.sourceId ?? "playlist",
      onClosedByUser,
    );

    // Pause this player if another player on the page starts
    const handlePauseAllPlayers = (e: Event) => {
      const event = e as CustomEvent<{ sourceId: string }>;
      if (event.detail.sourceId === playerRefId.current) return;

      if (typeof player.stop === "function") {
        player.stop();
      } else {
        player.playbackState = "paused";
      }
    };

    // Notify other players to pause when this one starts playing
    const handlePlaybackPlaying = () => {
      setIsPlaying(true);
      window.dispatchEvent(
        new CustomEvent("pauseAllPlayers", {
          detail: { sourceId: playerRefId.current },
        }),
      );
    };

    player.addEventListener("PlaybackPlaying", handlePlaybackPlaying);
    window.addEventListener(
      "pauseAllPlayers",
      handlePauseAllPlayers as EventListener,
    );

    return () => {
      player.destroy();
      inlineTarget.remove();
      cleanWidgetTarget?.remove();
      window.removeEventListener(
        "pauseAllPlayers",
        handlePauseAllPlayers as EventListener,
      );
      playerInstanceRef.current = null;
      initializedRef.current = false;
    };
  }, [isScriptReady, playlistItems, bhAudioTracking]);

  return {
    playerRef,
    playerInstance: playerInstanceRef,
    isPlaying,
  };
}
